import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  ReactNode,
} from 'react';
import Cookies from 'js-cookie';
import { Tenant } from '@repo/database/browser';
import { SELECTED_TENANT } from '../constants/tokens';
import { useQuery } from '@tanstack/react-query';
import { authMeQueryOptions } from '@/api/auth/auth-query';
import { useSentryUser } from '@/lib/sentry/use-sentry-user';
import type { PermissionPreviewInfo } from '@repo/utils';

interface ImpersonationInfo {
  adminId: string;
  adminUsername: string;
  adminRole: string;
  tenantId: string;
  impersonatedAt: number;
}

interface TenantContextType {
  selectedTenant: Tenant | null;
  setSelectedTenant: (tenant: Tenant | null) => void;
  clearSelectedTenant: () => void;
  impersonationInfo: ImpersonationInfo | null;
  /** Anteprima permessi attiva sulla sessione, da `/auth/me`. */
  permissionPreview: PermissionPreviewInfo | null;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

interface TenantProviderProps {
  children: ReactNode;
}

export const TenantProvider: React.FC<TenantProviderProps> = ({ children }) => {
  const [selectedTenant, setSelectedTenantState] = useState<Tenant | null>(
    () => {
      const storedTenant = Cookies.get(SELECTED_TENANT);
      if (storedTenant) {
        try {
          return JSON.parse(storedTenant);
        } catch (error) {
          console.error('Error parsing stored tenant:', error);
          Cookies.remove(SELECTED_TENANT);
          return null;
        }
      }
      return null;
    }
  );

  const { data: authData } = useQuery(authMeQueryOptions);
  const impersonationInfo =
    (authData?.impersonation as ImpersonationInfo) ?? null;
  const permissionPreview = authData?.permissionPreview ?? null;

  useSentryUser({
    userId: authData?.user?.id as string | undefined,
    email: authData?.user?.username as string | undefined,
    tenantId: selectedTenant?.id,
  });

  // Identità stabili: chi le mette nelle dipendenze di un effetto (es.
  // `/impersonate`) non deve rieseguirlo a ogni render del provider.
  const setSelectedTenant = useCallback((tenant: Tenant | null) => {
    setSelectedTenantState(tenant);
    if (tenant) {
      Cookies.set(SELECTED_TENANT, JSON.stringify({ id: tenant.id }), {
        expires: 30,
      });
    } else {
      Cookies.remove(SELECTED_TENANT);
    }
  }, []);

  const clearSelectedTenant = useCallback(() => {
    setSelectedTenant(null);
  }, [setSelectedTenant]);

  const value = useMemo(
    () => ({
      selectedTenant,
      setSelectedTenant,
      clearSelectedTenant,
      impersonationInfo,
      permissionPreview,
    }),
    [
      selectedTenant,
      setSelectedTenant,
      clearSelectedTenant,
      impersonationInfo,
      permissionPreview,
    ]
  );

  return (
    <TenantContext.Provider value={value}>{children}</TenantContext.Provider>
  );
};

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};
